import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { flagAccountSuspicious, getAccountDetail } from '../api'
import type { FlagSuspiciousPayload } from '../types'

export const accountDetailQueryKey = (customerId: string, nuban: string) =>
  ['customers', customerId, 'accounts', nuban] as const

export function useAccountDetail(
  customerId: string | undefined,
  nuban: string | undefined,
) {
  return useQuery({
    queryKey: ['customers', customerId ?? '', 'accounts', nuban ?? ''] as const,
    queryFn: () => getAccountDetail(customerId!, nuban!),
    enabled:
      customerId !== undefined &&
      customerId !== '' &&
      nuban !== undefined &&
      nuban !== '',
    staleTime: 30_000,
  })
}

/**
 * AF-132 — Flag as Suspicious. Creates a manual alert tied to this account
 * and returns the alert id so the UI can deep-link to it.
 */
export function useFlagAccountSuspicious() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: FlagSuspiciousPayload) => flagAccountSuspicious(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts'] })
      void qc.invalidateQueries({ queryKey: ['customers'] })
      toast.success('Manual alert created')
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Couldn't flag account"),
  })
}
